import { auth } from "./firebase";

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/blogs`;

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  image_url?: string;
  publishDate: string;
  readTime: number;
}

interface BlogFormData {
  title: string;
  content: string;
  excerpt: string;
  author: string;
  image_url?: string;
}

async function handleResponse(response: Response) {
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorData.message || 'Unknown error'}`);
  }
  return response.json();
}

async function getAuthHeaders() {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }
  return { 'Content-Type': 'application/json' };
}

export async function fetchAllBlogs(): Promise<BlogPost[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(BASE_URL, { headers });
    const result = await handleResponse(response);
    console.log('API Response for fetchAllBlogs:', result);

    // Handle the API response structure with data property
    const data = result.data || result;

    // Ensure we always return an array
    if (!Array.isArray(data)) {
      console.warn('API returned non-array data:', data);
      return [];
    }

    return data;
  } catch (error) {
    console.error('Error fetching all blogs:', error);
    throw error;
  }
}

export async function fetchSingleBlog(id: string): Promise<BlogPost> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/${id}`, { headers });
    const result = await handleResponse(response);

    // Handle the API response structure with data property
    return result.data || result;
  } catch (error) {
    console.error(`Error fetching blog with ID ${id}:`, error);
    throw error;
  }
}

export async function createBlog(blogData: BlogFormData): Promise<BlogPost> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(blogData),
    });
    const result = await handleResponse(response);

    // Handle the API response structure with data property
    return result.data || result;
  } catch (error) {
    console.error('Error creating blog:', error);
    throw error;
  }
}

export async function updateBlog(id: string, updatedBlogData: BlogFormData): Promise<BlogPost> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/${id}`, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(updatedBlogData),
    });
    const result = await handleResponse(response);

    // Handle the API response structure with data property
    return result.data || result;
  } catch (error) {
    console.error(`Error updating blog with ID ${id}:`, error);
    throw error;
  }
}

export async function deleteBlog(id: string): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${BASE_URL}/${id}`, {
      method: 'DELETE',
      headers: headers,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorData.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error(`Error deleting blog with ID ${id}:`, error);
    throw error;
  }
}
